import { CloseOutlined } from '@ant-design/icons'
import {
  NOTIFICATION_CONDITION_TYPES,
  type NotificationConditionType,
} from '@cims-online/shared'
import { Form, Row, Typography } from 'antd'
import CIMSButton from 'components/CIMSButton'
import CIMSFormItem from 'components/CIMSFormItem'
import type { NotificationField, NotificationStatus } from '../../../../types'
import SettingsCard from '../SettingsCard'
import ConditionTypeSettings from './components/ConditionTypeSettings'
import ConditionTypeSelect from './components/ConditionTypeSelect'
import styles from './index.module.css'

type ConditionsProps = {
  fields: NotificationField[]
  statuses: NotificationStatus[]
}

function isConditionType(
  value: unknown
): value is NotificationConditionType['type'] {
  return (
    typeof value === 'string' &&
    NOTIFICATION_CONDITION_TYPES.some(
      (conditionType) => conditionType === value
    )
  )
}

/**
 * Renders the ordered record conditions within the notification form.
 *
 * Each list item retains hidden drafts when its type changes, while the parent
 * submission normalizer emits only the settings for the selected type.
 */
function Conditions({ fields, statuses }: ConditionsProps) {
  return (
    <Form.List name="conditions">
      {(conditionFields, { add, remove }) => (
        <>
          {conditionFields.map(({ key, name }, index) => (
            <div key={key}>
              <Row justify="center" style={{ margin: '16px 0' }}>
                <Typography.Text type="secondary">And</Typography.Text>
              </Row>
              <div className={styles.cardWrapper}>
                <CIMSButton
                  aria-label={`Remove condition ${index + 1}`}
                  className={styles.removeButton}
                  icon={<CloseOutlined />}
                  onClick={() => remove(name)}
                  type="text"
                />
                <SettingsCard>
                  <CIMSFormItem
                    label="If"
                    name={[name, 'type']}
                    requiredMessage="Please select a condition"
                  >
                    <ConditionTypeSelect conditionName={name} fields={fields} />
                  </CIMSFormItem>
                  <Form.Item
                    noStyle
                    shouldUpdate={(before, after) =>
                      before.conditions?.[name]?.type !==
                      after.conditions?.[name]?.type
                    }
                  >
                    {({ getFieldValue }) => {
                      const type = getFieldValue([
                        'conditions',
                        name,
                        'type',
                      ]) as unknown

                      return isConditionType(type) ? (
                        <ConditionTypeSettings
                          conditionName={name}
                          fields={fields}
                          statuses={statuses}
                          type={type}
                        />
                      ) : null
                    }}
                  </Form.Item>
                </SettingsCard>
              </div>
            </div>
          ))}
          <Row justify="center" style={{ marginTop: 16 }}>
            <CIMSButton onClick={() => add({})}>Add Condition</CIMSButton>
          </Row>
        </>
      )}
    </Form.List>
  )
}

export default Conditions
